import React, { useState, useCallback } from 'react'
import { api } from '../../utils/api'
import { ProgressBar } from '../shared'

function ShopifyPublications() {
  const [loading, setLoading] = useState(false)
  const [loadStatus, setLoadStatus] = useState('')
  const [error, setError] = useState(null)
  const [report, setReport] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [publishing, setPublishing] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' })
  const [viewMode, setViewMode] = useState('partial')

  const loadReport = useCallback(async () => {
    setLoading(true)
    setError(null)
    setReport(null)

    try {
      // Step 1: Get publications list
      setLoadStatus('Fetching sales channels...')
      const pubData = await api.get('/api/inventory/shopify/publications')
      const publications = pubData.publications || []

      // Step 2: Get all active products
      setLoadStatus('Fetching active products...')
      const prodData = await api.get('/api/inventory/shopify/active-product-ids')
      const allProducts = prodData.products || []
      const productMap = {}
      for (const p of allProducts) {
        productMap[p.id] = p
      }

      // Step 3: For each publication, get its product list
      const pubProductSets = {} // pubId -> Set of product IDs
      for (let i = 0; i < publications.length; i++) {
        const pub = publications[i]
        setLoadStatus(`Scanning channel ${i + 1}/${publications.length}: ${pub.name}...`)
        try {
          const data = await api.get(`/api/inventory/shopify/publication-products/${encodeURIComponent(pub.id)}`)
          pubProductSets[pub.id] = new Set(data.productIds || [])
        } catch (err) {
          console.error(`Failed to load products for ${pub.name}:`, err)
          pubProductSets[pub.id] = new Set()
        }
      }

      // Step 4: Build report client-side
      setLoadStatus('Building report...')
      const fullyPublished = []
      const partiallyPublished = []
      const unpublished = []

      for (const product of allProducts) {
        const publishedTo = publications.filter(pub => pubProductSets[pub.id]?.has(product.id))
        const missingFrom = publications.filter(pub => !pubProductSets[pub.id]?.has(product.id))

        const entry = {
          id: product.id,
          title: product.title,
          image: product.image,
          publishedCount: publishedTo.length,
          totalChannels: publications.length,
          missingChannels: missingFrom.map(c => c.name)
        }

        if (missingFrom.length === 0) {
          fullyPublished.push(entry)
        } else if (publishedTo.length > 0) {
          partiallyPublished.push(entry)
        } else {
          unpublished.push(entry)
        }
      }

      setReport({
        publications,
        summary: {
          total: allProducts.length,
          fullyPublished: fullyPublished.length,
          partiallyPublished: partiallyPublished.length,
          unpublished: unpublished.length
        },
        fullyPublished,
        partiallyPublished,
        unpublished
      })
      setSelectedIds(new Set())
    } catch (err) {
      setError(err.message || JSON.stringify(err))
    } finally {
      setLoading(false)
      setLoadStatus('')
    }
  }, [])

  const publishProducts = useCallback(async (productIds) => {
    setPublishing(true)
    setProgress({ current: 0, total: productIds.length, status: 'Publishing...' })

    const batchSize = 5
    let published = 0
    let failed = 0

    for (let i = 0; i < productIds.length; i += batchSize) {
      const batch = productIds.slice(i, i + batchSize)
      try {
        const result = await api.post('/api/inventory/shopify/publish-to-channels', {
          productIds: batch
        })
        published += result.published || 0
        failed += result.failed || 0
      } catch (err) {
        failed += batch.length
      }
      setProgress({
        current: Math.min(i + batchSize, productIds.length),
        total: productIds.length,
        status: `Published ${published}, failed ${failed}`
      })
    }

    setPublishing(false)
    setProgress({ current: 0, total: 0, status: '' })
    await loadReport()
    alert(`Done! Published: ${published}, Failed: ${failed}`)
  }, [loadReport])

  const handlePublishOne = useCallback((productId) => {
    publishProducts([productId])
  }, [publishProducts])

  const handleBulkPublish = useCallback(() => {
    if (selectedIds.size === 0) return
    publishProducts(Array.from(selectedIds))
  }, [selectedIds, publishProducts])

  const handlePublishAll = useCallback(() => {
    if (!report) return
    const allNeedsPublishing = [
      ...report.partiallyPublished,
      ...report.unpublished
    ].map(p => p.id)
    if (allNeedsPublishing.length === 0) return
    if (!confirm(`Publish ${allNeedsPublishing.length} products to all channels?`)) return
    publishProducts(allNeedsPublishing)
  }, [report, publishProducts])

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback((products) => {
    setSelectedIds(prev => {
      const allIds = products.map(p => p.id)
      const allSelected = allIds.every(id => prev.has(id))
      if (allSelected) return new Set()
      return new Set(allIds)
    })
  }, [])

  const getVisibleProducts = () => {
    if (!report) return []
    if (viewMode === 'partial') return report.partiallyPublished
    if (viewMode === 'unpublished') return report.unpublished
    return [...report.partiallyPublished, ...report.unpublished]
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
        <div>
          <h3 style={{ margin: 0 }}>Shopify Sales Channel Publications</h3>
          <p style={{ margin: '4px 0 0', color: 'var(--color-text-muted)', fontSize: 13 }}>
            Check and manage which sales channels your products are published to
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={loadReport}
          disabled={loading}
        >
          {loading ? 'Scanning...' : report ? 'Refresh Report' : 'Load Report'}
        </button>
      </div>

      {loading && loadStatus && (
        <div style={{
          padding: 'var(--space-3)',
          background: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius)',
          marginBottom: 'var(--space-4)',
          fontSize: 13,
          fontWeight: 500
        }}>
          {loadStatus}
        </div>
      )}

      {error && (
        <div style={{
          padding: 'var(--space-4)',
          background: '#fef2f2',
          color: '#dc2626',
          borderRadius: 'var(--radius)',
          marginBottom: 'var(--space-4)',
          border: '2px solid #dc2626',
          fontSize: 14,
          fontWeight: 600
        }}>
          ERROR: {error}
        </div>
      )}

      {publishing && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <ProgressBar current={progress.current} total={progress.total} status={progress.status} />
        </div>
      )}

      {report && (
        <>
          {/* Summary Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)'
          }}>
            <div style={{
              padding: 'var(--space-3)',
              background: 'var(--color-bg-secondary)',
              borderRadius: 'var(--radius)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{report.summary.total}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Total Products</div>
            </div>
            <div style={{
              padding: 'var(--space-3)',
              background: '#f0fdf4',
              borderRadius: 'var(--radius)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#16a34a' }}>{report.summary.fullyPublished}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Fully Published</div>
            </div>
            <div style={{
              padding: 'var(--space-3)',
              background: '#fffbeb',
              borderRadius: 'var(--radius)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#d97706' }}>{report.summary.partiallyPublished}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Partially Published</div>
            </div>
            <div style={{
              padding: 'var(--space-3)',
              background: '#fef2f2',
              borderRadius: 'var(--radius)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>{report.summary.unpublished}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Unpublished</div>
            </div>
          </div>

          {/* Available Channels */}
          <div style={{
            padding: 'var(--space-3)',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius)',
            marginBottom: 'var(--space-4)',
            fontSize: 13
          }}>
            <strong>Channels ({report.publications.length}):</strong>{' '}
            {report.publications.map(p => p.name).join(', ')}
          </div>

          {/* View Mode Tabs + Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
            <div style={{
              display: 'flex',
              gap: 'var(--space-2)',
              background: 'var(--color-bg-secondary)',
              padding: 'var(--space-1)',
              borderRadius: 'var(--radius)',
              width: 'fit-content'
            }}>
              {[
                { id: 'partial', label: `Partial (${report.summary.partiallyPublished})` },
                { id: 'unpublished', label: `Unpublished (${report.summary.unpublished})` },
                { id: 'all', label: `All Needing Publish (${report.summary.partiallyPublished + report.summary.unpublished})` }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setViewMode(tab.id); setSelectedIds(new Set()) }}
                  style={{
                    padding: 'var(--space-1) var(--space-3)',
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    background: viewMode === tab.id ? 'white' : 'transparent',
                    color: viewMode === tab.id ? 'var(--green-onions)' : 'var(--color-text-muted)',
                    boxShadow: viewMode === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              {selectedIds.size > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={handleBulkPublish}
                  disabled={publishing}
                >
                  Publish Selected ({selectedIds.size})
                </button>
              )}
              {(report.summary.partiallyPublished + report.summary.unpublished) > 0 && (
                <button
                  className="btn"
                  onClick={handlePublishAll}
                  disabled={publishing}
                  style={{ background: '#dc2626', color: 'white', border: 'none' }}
                >
                  Publish All ({report.summary.partiallyPublished + report.summary.unpublished})
                </button>
              )}
            </div>
          </div>

          {/* Products Table */}
          <ProductsTable
            products={getVisibleProducts()}
            selectedIds={selectedIds}
            toggleSelect={toggleSelect}
            toggleSelectAll={toggleSelectAll}
            onPublish={handlePublishOne}
            publishing={publishing}
          />
        </>
      )}

      {!report && !loading && (
        <div style={{
          textAlign: 'center',
          padding: 'var(--space-8)',
          color: 'var(--color-text-muted)'
        }}>
          Click "Load Report" to scan your Shopify products and check their publication status.
          <br />
          <span style={{ fontSize: 12 }}>This may take a moment for large catalogues.</span>
        </div>
      )}
    </div>
  )
}

function ProductsTable({ products, selectedIds, toggleSelect, toggleSelectAll, onPublish, publishing }) {
  if (products.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
        No products in this category.
      </div>
    )
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
            <th style={{ padding: 'var(--space-2)', textAlign: 'left', width: 40 }}>
              <input
                type="checkbox"
                checked={products.length > 0 && products.every(p => selectedIds.has(p.id))}
                onChange={() => toggleSelectAll(products)}
              />
            </th>
            <th style={{ padding: 'var(--space-2)', textAlign: 'left' }}>Product</th>
            <th style={{ padding: 'var(--space-2)', textAlign: 'center', width: 100 }}>Published</th>
            <th style={{ padding: 'var(--space-2)', textAlign: 'left' }}>Missing Channels</th>
            <th style={{ padding: 'var(--space-2)', textAlign: 'right', width: 100 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map(product => (
            <tr key={product.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(product.id)}
                  onChange={() => toggleSelect(product.id)}
                />
              </td>
              <td style={{ padding: 'var(--space-2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  {product.image && (
                    <img
                      src={product.image}
                      alt=""
                      style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }}
                    />
                  )}
                  <span style={{ fontWeight: 500 }}>{product.title}</span>
                </div>
              </td>
              <td style={{ padding: 'var(--space-2)', textAlign: 'center' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  background: product.publishedCount === product.totalChannels ? '#dcfce7' :
                             product.publishedCount > 0 ? '#fef3c7' : '#fee2e2',
                  color: product.publishedCount === product.totalChannels ? '#16a34a' :
                        product.publishedCount > 0 ? '#d97706' : '#dc2626'
                }}>
                  {product.publishedCount}/{product.totalChannels}
                </span>
              </td>
              <td style={{ padding: 'var(--space-2)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {product.missingChannels.map(ch => (
                    <span key={ch} style={{
                      display: 'inline-block',
                      padding: '1px 6px',
                      borderRadius: 4,
                      fontSize: 11,
                      background: '#fee2e2',
                      color: '#991b1b'
                    }}>
                      {ch}
                    </span>
                  ))}
                </div>
              </td>
              <td style={{ padding: 'var(--space-2)', textAlign: 'right' }}>
                <button
                  className="btn btn-sm"
                  onClick={() => onPublish(product.id)}
                  disabled={publishing}
                  style={{ fontSize: 12, padding: '4px 10px' }}
                >
                  Publish
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default ShopifyPublications
